import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Text } from '../../../components/Typography/Typography';
import Pagination from './components/Pagination';
import EvaluationsHeader from './components/EvaluationsHeader';
import EvaluationsFilters from './components/EvaluationsFilters';
import EvaluationsTable from './components/EvaluationsTable';
import { getEvaluations } from '../../../api/evaluationApi';
import { setLoading, setError, setEvaluationsData } from '../../../store/evaluation/slice';
import { RootState } from '../../../store';
import styles from './EvaluationsPage.module.scss';

const ITEMS_PER_PAGE = 25;

const EvaluationsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { loading, error, total } = useSelector((state: RootState) => state.evaluation);

  const currentPage = parseInt(searchParams.get('page') || '1');

  const fetchEvaluations = async () => {
    try {
      dispatch(setLoading(true));
      const { data, pagination } = await getEvaluations({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        trigger_type: searchParams.get('trigger_type') || undefined,
        trigger_id: searchParams.get('trigger_id') || undefined,
      });
      dispatch(setEvaluationsData({ data, total: pagination.total }));
      dispatch(setError(null));
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : 'An error occurred'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchEvaluations();
  }, [searchParams]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage > 1) {
      searchParams.set('page', newPage.toString());
    } else {
      searchParams.delete('page');
    }
    setSearchParams(searchParams);
  };

  if (error) {
    return (
      <div className={styles.error}>
        <Text color="red">Error: {error}</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <EvaluationsHeader />
      <EvaluationsFilters searchParams={searchParams} setSearchParams={setSearchParams} />

      {loading ? (
        <div className={styles.loading}>
          <Text color="neutral-600">Loading...</Text>
        </div>
      ) : (
        <>
          <EvaluationsTable />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={total}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </>
      )}
    </div>
  );
};

export default EvaluationsPage;
